<template>
    <div class="animated fadeIn">
        <b-card>
            <template v-slot:header>
                <h5 class="mb-0">Kas Kecil</h5>
            </template>

            <b-alert v-if="ecDeadlineAlert" show variant="danger" class="mb-3">
                {{ ecDeadlineAlert }}
            </b-alert>

            <TabsUnderline v-model="activeTab" :tabs="tabOptions" @input="onTabChange" />

            <div v-show="activeTab === 'list'">
            <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
            <div v-else>

            <div class="big-filter-label filter-form-wrapper mb-3">
                <b-row>
                    <b-col cols="12" md="6">
                        <b-form-group label="Search">
                            <b-form-input
                                v-model.trim="filters.search"
                                type="search"
                                name="kasKecilFilterSearch"
                                placeholder="Cari nomor CA / nomor EC"
                                @keyup.enter="onSearch"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col v-if="showCabangFilter" cols="12" md="6">
                        <b-form-group label="Cabang">
                            <multiselect
                                v-model="filters.cabang"
                                :options="cabangOptions"
                                :internal-search="true"
                                :allow-empty="false"
                                deselect-label=""
                                select-label=""
                                label="name"
                                track-by="name"
                                name="kasKecilFilterCabang"
                                placeholder="Semua Cabang"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                        <b-form-group label="Status">
                            <multiselect
                                v-model="filters.status"
                                :options="statusOptions"
                                :internal-search="true"
                                :allow-empty="false"
                                deselect-label=""
                                select-label=""
                                label="text"
                                track-by="value"
                                name="kasKecilFilterStatus"
                                placeholder="Semua"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                        <b-form-group label="Tanggal Dibuat">
                            <div class="d-flex kas-kecil-date-filter">
                                <b-form-input
                                    v-model="filters.startDate"
                                    type="date"
                                    name="kasKecilFilterTanggalMulai"
                                    class="flex-fill"
                                />
                                <span class="ml-2 mr-2 d-flex align-items-center">s/d</span>
                                <b-form-input
                                    v-model="filters.endDate"
                                    type="date"
                                    name="kasKecilFilterTanggalAkhir"
                                    class="flex-fill"
                                />
                            </div>
                        </b-form-group>
                    </b-col>
                </b-row>
                <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div class="d-flex flex-wrap gap-2">
                        <b-button
                            variant="pgiBtn"
                            :disabled="isBusy"
                            name="kasKecilSearchBtn"
                            @click="onSearch"
                        >
                            Cari
                        </b-button>
                        <b-button
                            variant="pgiBtn"
                            :disabled="isBusy"
                            name="kasKecilResetBtn"
                            @click="onReset"
                        >
                            Reset
                        </b-button>
                    </div>
                    <div class="d-flex flex-wrap gap-2">
                        <b-button
                            v-if="hasMiniCashExportPermission"
                            variant="success"
                            name="kasKecilExportLaporanBtn"
                            :disabled="isSubmittingExportLaporan"
                            @click="onExportLaporan"
                        >
                            <b-spinner v-if="isSubmittingExportLaporan" small class="mr-1"></b-spinner>
                            <i v-else class="fa fa-file-excel-o mr-1"></i>
                            Export Laporan
                        </b-button>
                        <b-button
                            v-if="hasMiniCashRequestPermission"
                            variant="pgiBtn"
                            name="kasKecilRequestBtn"
                            @click="onRequestKasKecil"
                        >
                            Ajukan CA
                        </b-button>
                    </div>
                </div>
            </div>

            <b-table
                id="kas-kecil-table"
                bordered
                hover
                show-empty
                responsive
                :busy="isBusy"
                :items="items"
                :fields="fields"
                class="mb-3"
            >
                <template v-slot:table-busy>
                    <div class="text-center text-danger my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong>Loading data...</strong>
                    </div>
                </template>
                <template v-slot:empty>
                    Tidak ada data kas kecil.
                </template>
                <template v-slot:cell(cabang)="row">
                    <div>{{ getCabangName(row.item) }}</div>
                    <div v-if="!$helper.isNullOrEmpty(row.item.numberCa)" class="smallText text-muted">
                        Nomor CA: {{ row.item.numberCa }}
                    </div>
                    <div v-if="!$helper.isNullOrEmpty(row.item.numberEc)" class="smallText text-muted">
                        Nomor EC: {{ row.item.numberEc }}
                    </div>
                </template>
                <template v-slot:cell(nominal)="row">
                    <div class="kas-kecil-nominal-row">
                        <span class="smallText text-muted">Nominal Pengajuan</span>
                        <span class="smallText">{{ formatCurrency(row.item.requestedCash) }}</span>
                    </div>
                </template>
                <template v-slot:cell(status)="row">
                    <div>
                        <span :class="getStatusClass(row.item.status)">
                            {{ getStatusLabel(row.item.status) }}
                        </span>
                        <div v-if="showTotalPendingKc(row.item)" class="smallText text-muted mt-1">
                            Perlu Dicek KC: {{ row.item.totalPendingKc || 0 }} Barang/Jasa
                        </div>
                        <div v-if="hasAllDocs(row.item)" class="smallText text-muted mt-1">
                            Dokumen: <span class="text-success">Sudah Diunggah</span>
                        </div>
                    </div>
                </template>
                <template v-slot:cell(action)="row">
                    <div class="kas-kecil-action-wrapper">
                        <b-button
                            v-if="canApproveCA(row.item)"
                            size="sm"
                            variant="success"
                            name="kasKecilApproveCaBtn"
                            @click="openCaConfirm(row.item)"
                        >
                            Setujui CA
                        </b-button>
                        <b-button
                            v-if="canClaimCash(row.item)"
                            size="sm"
                            variant="success"
                            name="kasKecilClaimCashBtn"
                            @click="onClaimCash(row.item)"
                        >
                            Klaim Kas
                        </b-button>
                        <b-button
                            v-if="canProcessPengembalian(row.item)"
                            size="sm"
                            variant="warning"
                            class="text-white"
                            name="kasKecilProcessPengembalianBtn"
                            @click="openPengembalianConfirm(row.item)"
                        >
                            Proses Pengembalian
                        </b-button>
                        <b-button
                            size="sm"
                            class="btn-dokumen-ca"
                            name="kasKecilDokumenCaBtn"
                            title="Dokumen CA"
                            :disabled="exportingCaId === row.item.id"
                            @click="exportCa(row.item)"
                        >
                            <b-spinner v-if="exportingCaId === row.item.id" small class="mr-1 btn-icon-spinner"></b-spinner>
                            <i v-else class="fa fa-file-text-o mr-1"></i>
                            Dokumen CA
                        </b-button>
                        <b-button
                            v-if="canUploadEc(row.item) && !hasAnyDocs(row.item)"
                            size="sm"
                            variant="success"
                            name="kasKecilUploadEcBtn"
                            title="Upload Dokumen"
                            @click="openUploadEc(row.item)"
                        >
                            <i class="fa fa-upload mr-1"></i>
                            Upload Dokumen
                        </b-button>
                        <b-button
                            v-if="hasAllDocs(row.item)"
                            size="sm"
                            variant="success"
                            name="kasKecilDokumenEcBtn"
                            title="Lihat Dokumen"
                            @click="onShowDocsEc(row.item)"
                        >
                            <i class="fa fa-file-text-o mr-1"></i>
                            Lihat Dokumen
                        </b-button>
                        <b-button
                            size="sm"
                            variant="pgiBtn"
                            name="kasKecilRiwayatBtn"
                            @click="openHistory(row.item)"
                        >
                            Riwayat
                        </b-button>
                        <b-button
                            size="sm"
                            variant="primary"
                            name="kasKecilDetailBtn"
                            @click="openDetail(row.item)"
                        >
                            Detail
                        </b-button>
                    </div>
                </template>
            </b-table>

            <div class="pagination-wrapper d-flex align-items-center justify-content-between">
                <b-input-group prepend="Tampilkan" class="align-items-center tableShowItem">
                    <b-form-select v-model="show" @change="onLimitChange" :options="optionsLimit">
                        <template v-slot:first>
                            <b-form-select-option :value="null" disabled />
                        </template>
                    </b-form-select>
                    <span class="ml-2">{{ totalViewText }}</span>
                </b-input-group>
                <b-pagination
                    class="mb-0"
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    aria-controls="kas-kecil-table"
                    @input="onPageChange"
                />
            </div>

            </div>
            </div>

            <div v-show="activeTab === 'void-pengembalian'">
                <KasKecilVoidPengembalian v-if="voidTabMounted" :key="voidTabKey" @refunded="onVoidPengembalianRefunded" />
            </div>
        </b-card>

        <ModalKasKecilExportLaporanConfirm :isSubmitting="isSubmittingExportLaporan" @submit="submitExportLaporan" />
        <ModalKasKecilRequestCa :isSubmitting="isSubmittingRequest" @submit="submitRequestKasKecil" />
        <ModalKasKecilClaimCash :desc="claimCashConfirmDesc" :isSubmitting="isSubmittingClaimCash" @submit="submitClaimCash" />
        <ModalKasKecilCaApproval :desc="caConfirmDesc" :isSubmitting="isSubmittingCaApproval" @submit="submitCaApproval" />
        <ModalKasKecilPengembalian :desc="pengembalianConfirmDesc" :isSubmitting="isSubmittingPengembalian" @submit="submitPengembalian" />
        <ModalKasKecilRiwayat :items="historyTimeline" />
        <ModalKasKecilUploadEc :itemId="selectedUploadEcItemId" @success="onUploadEcSuccess" />
        <ModalKasKecilLogPhotoPreview
            modalId="modal-preview-log-kas-kecil-ec"
            :title="activeDocsEcPreview.title"
            :url="activeDocsEcPreview.url"
            :files="activeDocsEcPreview.files"
            @switch-photo="setActiveDocsEcPhoto"
        />
        <ModalKasKecilFilePreview
            id="modal-preview-export-ca"
            title="Dokumen CA"
            :url="exportCaUrl"
            type="pdf"
        />
    </div>
</template>

<script>
import CONSTANTS from '@/constants'
import ModalKasKecilExportLaporanConfirm from '@/components/modals/ModalKasKecilExportLaporanConfirm'
import ModalKasKecilRequestCa from '@/components/modals/ModalKasKecilRequestCa'
import ModalKasKecilClaimCash from '@/components/modals/ModalKasKecilClaimCash'
import ModalKasKecilCaApproval from '@/components/modals/ModalKasKecilCaApproval'
import ModalKasKecilPengembalian from '@/components/modals/ModalKasKecilPengembalian'
import ModalKasKecilRiwayat from '@/components/modals/ModalKasKecilRiwayat'
import ModalKasKecilFilePreview from '@/components/modals/ModalKasKecilFilePreview'
import ModalKasKecilUploadEc from '@/components/modals/ModalKasKecilUploadEc'
import ModalKasKecilLogPhotoPreview from '@/components/modals/ModalKasKecilLogPhotoPreview'
import KasKecilVoidPengembalian from '@/views/kasir/kas-kecil/VoidPengembalian'
import TabsUnderline from '@/components/common/TabsUnderline'

const STATUS_KAS_KECIL = CONSTANTS.STATUS_KAS_KECIL

export default {
    name: "kas-kecil-list",
    components: {
        KasKecilVoidPengembalian,
        TabsUnderline,
        ModalKasKecilExportLaporanConfirm,
        ModalKasKecilRequestCa,
        ModalKasKecilClaimCash,
        ModalKasKecilCaApproval,
        ModalKasKecilPengembalian,
        ModalKasKecilRiwayat,
        ModalKasKecilFilePreview,
        ModalKasKecilUploadEc,
        ModalKasKecilLogPhotoPreview,
    },
    data() {
        return {
            activeTab: "list",
            ecDeadlineAlert: null,
            voidPengembalianCount: 0,
            voidTabMounted: false,
            voidTabKey: 0,
            initStatus: 0,
            isBusy: false,
            isSubmittingRequest: false,
            isSubmittingExportLaporan: false,
            isSubmittingClaimCash: false,
            isSubmittingCaApproval: false,
            isSubmittingPengembalian: false,
            selectedClaimCashItem: null,
            selectedCaItem: null,
            selectedPengembalianItem: null,
            selectedHistoryItem: null,
            filters: {
                cabang: null,
                status: { value: null, text: "Semua" },
                startDate: null,
                endDate: null,
                search: "",
            },
            cabangOptions: [],
            statusOptions: [
                { value: null, text: "Semua" },
                { value: STATUS_KAS_KECIL.PENDING, text: "Menunggu Approval CA" },
                { value: STATUS_KAS_KECIL.BERJALAN, text: "Berjalan" },
                { value: STATUS_KAS_KECIL.REQUEST_CLOSED, text: "Menunggu Approval EC" },
                { value: STATUS_KAS_KECIL.APPROVED_KC, text: "Disetujui KC" },
                { value: STATUS_KAS_KECIL.PENDING_PENGEMBALIAN, text: "Menunggu Pengembalian" },
                { value: STATUS_KAS_KECIL.CLOSED, text: "Ditutup" },
            ],
            fields: [
                { key: "cabang", label: "Cabang", thClass: "min-w-160", tdClass: "min-w-160" },
                { key: "nominal", label: "Nominal", thClass: "min-w-180", tdClass: "min-w-180" },
                { key: "status", label: "Status", thClass: "min-w-140", tdClass: "min-w-140" },
                { key: "action", label: "Aksi", thClass: "min-w-160", tdClass: "min-w-160" },
            ],
            items: [],
            currentPage: 1,
            show: 10,
            perPage: 10,
            totalRows: 0,
            totalViewText: "0 dari 0 data",
            optionsLimit: CONSTANTS.OPTIONS_LIMIT_PAGINATION,
            exportCaUrl: "",
            exportingCaId: null,
            selectedUploadEcItemId: null,
            activeDocsEcPreview: {
                title: "Lihat Dokumen EC",
                url: "",
                files: [],
            },
        }
    },
    methods: {
        async init() {
            try {
                this.initStatus = 0
                this.getCabang()
                await Promise.all([
                    this.getItems(),
                    this.getVoidPengembalianCount(),
                    this.checkEcDeadline(),
                ])
                this.initStatus = 1
            } catch (error) {
                this.initStatus = -1
            }
        },
        getVoidPengembalianCount() {
            return this.$axios.get("mini-cash/auto-void/count/", {
                params: { cabangId: this.$helper.getCabangId() },
            }).then((response) => {
                this.voidPengembalianCount = Number(response.data || 0)
            })
        },
        checkEcDeadline() {
            return this.$axios.get("mini-cash/check-ec-deadline").then((response) => {
                const result = response.data || {}
                this.ecDeadlineAlert = Number(result.id) === 1 ? result.name : null
            })
        },
        onTabChange(value) {
            if (value === "list") {
                this.getItems(1)
                return
            }

            if (value === "void-pengembalian") {
                this.voidTabMounted = true
                this.voidTabKey += 1
            }
        },
        onVoidPengembalianRefunded() {
            this.getVoidPengembalianCount().catch(() => {})
        },
        buildQueryParams() {
            const params = {
                page: this.currentPage - 1,
                size: this.perPage,
            }
            const cabangId = this.filters.cabang ? this.filters.cabang.id : null
            const status = this.filters.status ? this.filters.status.value : null

            if (!this.$helper.isNullOrEmpty(cabangId)) params.filterCabangId = cabangId
            if (!this.$helper.isNullOrEmpty(status) || status === STATUS_KAS_KECIL.PENDING) params.filterStatus = status
            if (!this.$helper.isNullOrEmpty(this.filters.startDate)) params.filterStartDate = this.filters.startDate
            if (!this.$helper.isNullOrEmpty(this.filters.endDate)) params.filterEndDate = this.filters.endDate
            if (!this.$helper.isNullOrEmpty(this.filters.search)) params.filterNumber = this.filters.search

            return params
        },
        getCabang() {
            const storedCabang = localStorage.getItem("cabang")
            const cabang = storedCabang ? JSON.parse(storedCabang) : []
            const mappedCabang = Array.isArray(cabang)
                ? cabang.map(item => ({
                    ...item,
                    name: item.name ? item.name.replace(/^\(\d+\)\s*/, "") : "",
                }))
                : []

            this.cabangOptions = [
                { id: null, name: "Semua Cabang" },
                ...mappedCabang,
            ]
            this.filters.cabang = this.defaultCabangFilter
        },
        mapMiniCashItem(item) {
            return {
                ...item,
                cabang: {
                    id: item.cabangId,
                    name: item.namaCabang,
                },
            }
        },
        getItems(page = 1) {
            this.currentPage = isNaN(page) ? 1 : page
            this.isBusy = true
            this.items = []

            return this.$axios.get("mini-cash/", { params: this.buildQueryParams() })
                .then((response) => {
                    const result = response.data || {}
                    const data = Array.isArray(result.data) ? result.data : (Array.isArray(result.content) ? result.content : [])

                    this.items = data.map(this.mapMiniCashItem)
                    this.totalRows = Number(
                        result.total !== undefined
                            ? result.total
                            : (result.totalElements !== undefined ? result.totalElements : data.length)
                    )
                    this.updateTotalViewText()
                })
                .catch((error) => {
                    this.$helper.parseError(this, error)
                    throw error
                })
                .finally(() => {
                    this.isBusy = false
                })
        },
        updateTotalViewText() {
            if (this.totalRows === 0) {
                this.totalViewText = "0 dari 0 data"
                return
            }

            const pg = this.currentPage - 1
            const prev = pg * this.show + 1
            let limit = pg * this.show + this.show
            if (limit > this.totalRows) limit = this.totalRows
            this.totalViewText = `Menampilkan ${prev} - ${limit} dari ${this.totalRows} entri`
        },
        onSearch() {
            if (!this.validateDateRange()) return
            this.getItems(1)
        },
        onReset() {
            this.filters = {
                cabang: this.defaultCabangFilter,
                status: this.statusOptions[0],
                startDate: null,
                endDate: null,
                search: "",
            }
            this.getItems(1)
        },
        validateDateRange() {
            if (this.filters.startDate && this.filters.endDate && this.filters.startDate > this.filters.endDate) {
                this.$helper.toastErr(this, "Tanggal mulai tidak boleh lebih dari tanggal akhir")
                return false
            }

            return true
        },
        onLimitChange(newPerPage) {
            this.show = newPerPage
            this.perPage = newPerPage
            this.getItems(1)
        },
        onPageChange(newPage) {
            this.getItems(newPage)
        },
        onRequestKasKecil() {
            this.$bvModal.show("modal-confirm-request-kas-kecil")
        },
        submitRequestKasKecil() {
            const cabangId = this.$helper.getCabangId()
            if (this.$helper.isNullOrEmpty(cabangId)) {
                this.$helper.toastErr(this, "Cabang aktif tidak ditemukan.")
                return
            }

            this.isSubmittingRequest = true
            this.$axios.post("mini-cash/create", {}, {
                params: {
                    cabangId,
                },
            }).then(() => {
                this.$bvModal.hide("modal-confirm-request-kas-kecil")
                this.$helper.toastSucc(this, "Pengajuan CA berhasil dibuat.")
                this.getItems(1)
            }).catch((error) => {
                this.$helper.parseError(this, error)
            }).finally(() => {
                this.isSubmittingRequest = false
            })
        },
        onExportLaporan() {
            if (this.$helper.isNullOrEmpty(this.filters.startDate) || this.$helper.isNullOrEmpty(this.filters.endDate)) {
                this.$helper.toastErr(this, "Tanggal mulai dan tanggal akhir wajib diisi.")
                return
            }
            if (!this.validateDateRange()) return

            this.$bvModal.show("modal-confirm-export-laporan-kas-kecil")
        },
        submitExportLaporan() {
            this.isSubmittingExportLaporan = true
            this.$axios.get("mini-cash/export-mini-cash-log/", {
                params: {
                    startDate: this.filters.startDate,
                    endDate: this.filters.endDate,
                },
            }).then((response) => {
                const result = response.data || {}
                if (this.$helper.isNullOrEmpty(result.name)) {
                    this.$helper.toastErr(this, "Nama file tidak ditemukan dalam response.")
                    return
                }

                this.$bvModal.hide("modal-confirm-export-laporan-kas-kecil")
                this.downloadFile(this.buildBaseFileUrl(result.name), result.name)
            }).catch((error) => {
                this.$helper.parseError(this, error)
            }).finally(() => {
                this.isSubmittingExportLaporan = false
            })
        },
        downloadFile(url, fileName) {
            const element = document.createElement("a")
            element.setAttribute("href", url)
            element.setAttribute("download", fileName)
            element.style.display = "none"

            document.body.appendChild(element)
            element.click()
            document.body.removeChild(element)
        },
        openDetail(item) {
            if (item && item.id) {
                this.$router.push({ path: `/kasir/kas-kecil/${item.id}` })
            }
        },
        exportCa(item) {
            if (!item || !item.id) {
                this.$helper.toastErr(this, "ID kas kecil tidak ditemukan.")
                return
            }

            this.exportingCaId = item.id
            this.$axios.get("mini-cash/export-ca", {
                params: {
                    miniCashCabangId: item.id,
                },
            }).then((response) => {
                const result = response.data || {}
                if (result.name) {
                    this.exportCaUrl = this.buildBaseFileUrl(result.name)
                    this.$bvModal.show("modal-preview-export-ca")
                } else {
                    this.$helper.toastErr(this, "Nama file tidak ditemukan dalam response.")
                }
            }).catch((error) => {
                this.$helper.parseError(this, error)
            }).finally(() => {
                this.exportingCaId = null
            })
        },
        buildBaseFileUrl(path) {
            if (this.$helper.isNullOrEmpty(path)) return ""
            if (/^https?:\/\//.test(path)) return path

            const basePath = String(this.$baseFilePath || "").replace(/\/$/, "")
            const filePath = String(path).replace(/^\//, "")
            return `${basePath}/${filePath}`
        },
        openUploadEc(item) {
            if (!item || !item.id) {
                this.$helper.toastErr(this, "ID kas kecil tidak ditemukan.")
                return
            }

            this.selectedUploadEcItemId = item.id
            this.$bvModal.show("modal-upload-ec-kas-kecil")
        },
        onUploadEcSuccess() {
            this.getItems(this.currentPage)
        },
        hasDocsEc(item) {
            return Array.isArray(item.docsEc) && item.docsEc.length > 0
        },
        hasDocsCa(item) {
            return Array.isArray(item.docsCa) && item.docsCa.length > 0
        },
        hasAnyDocs(item) {
            return this.hasDocsEc(item) || this.hasDocsCa(item)
        },
        hasAllDocs(item) {
            return this.hasDocsEc(item) && this.hasDocsCa(item)
        },
        onShowDocsEc(item) {
            const ecFiles = (item.docsEc || [])
                .map((doc, index) => ({
                    label: `Dokumen EC ${index + 1}`,
                    url: this.buildDocsEcUrl(doc.path),
                }))
            const caFiles = (item.docsCa || [])
                .map((doc, index) => ({
                    label: `Dokumen CA ${index + 1}`,
                    url: this.buildDocsEcUrl(doc.path),
                }))
            const files = [...caFiles, ...ecFiles]
                .filter((file) => !this.$helper.isNullOrEmpty(file.url))

            if (files.length === 0) {
                this.$helper.toastErr(this, "Dokumen tidak tersedia.")
                return
            }

            this.activeDocsEcPreview = {
                title: "Lihat Dokumen",
                url: files[0].url,
                files,
            }
            this.$bvModal.show("modal-preview-log-kas-kecil-ec")
        },
        setActiveDocsEcPhoto(file) {
            this.activeDocsEcPreview.url = file.url
        },
        buildDocsEcUrl(path) {
            if (this.$helper.isNullOrEmpty(path)) return ""
            if (/^https?:\/\//.test(path)) return path

            const basePath = String(this.$baseFilePathAws || "").replace(/\/$/, "")
            const filePath = String(path).replace(/^\//, "")
            return `${basePath}/${filePath}`
        },
        openHistory(item) {
            this.selectedHistoryItem = item
            this.$bvModal.show("modal-riwayat-kas-kecil")
        },
        canClaimCash(item) {
            return this.normalizeStatus(item.status) === STATUS_KAS_KECIL.APPROVED_KC
                && this.$helper.hasPermissionMenuChildren("MRC")
        },
        canApproveCA(item) {
            return this.normalizeStatus(item.status) === STATUS_KAS_KECIL.PENDING
                && this.$helper.hasPermissionMenuChildren("AMC")
        },
        canProcessPengembalian(item) {
            return this.normalizeStatus(item.status) === STATUS_KAS_KECIL.PENDING_PENGEMBALIAN
                && this.$helper.hasPermissionMenuChildren("MPP")
        },
        canUploadEc(item) {
            return this.normalizeStatus(item.status) === STATUS_KAS_KECIL.CLOSED
                && this.$helper.hasPermissionMenuChildren("MPP")
        },
        showTotalPendingKc(item) {
            return [
                STATUS_KAS_KECIL.REQUEST_CLOSED,
                STATUS_KAS_KECIL.PENDING_PENGEMBALIAN,
                STATUS_KAS_KECIL.CLOSED,
            ].includes(this.normalizeStatus(item.status))
        },
        openCaConfirm(item) {
            this.selectedCaItem = item
            this.$bvModal.show("modal-confirm-ca-kas-kecil")
        },
        submitCaApproval() {
            if (!this.selectedCaItem || !this.selectedCaItem.id) {
                this.$helper.toastErr(this, "ID kas kecil tidak ditemukan.")
                return
            }

            this.isSubmittingCaApproval = true
            this.$axios.post("mini-cash/kc/approve", {}, {
                params: {
                    miniCashCabangId: this.selectedCaItem.id,
                },
            }).then(() => {
                this.$bvModal.hide("modal-confirm-ca-kas-kecil")
                this.$helper.toastSucc(this, "CA berhasil disetujui.")
                this.selectedCaItem = null
                this.getItems(this.currentPage)
            }).catch((error) => {
                this.$helper.parseError(this, error)
            }).finally(() => {
                this.isSubmittingCaApproval = false
            })
        },
        openPengembalianConfirm(item) {
            this.selectedPengembalianItem = item
            this.$bvModal.show("modal-confirm-pengembalian-kas-kecil")
        },
        submitPengembalian() {
            if (!this.selectedPengembalianItem || !this.selectedPengembalianItem.id) {
                this.$helper.toastErr(this, "ID kas kecil tidak ditemukan.")
                return
            }

            this.isSubmittingPengembalian = true
            this.$axios.post("mini-cash/proses-pengembalian", {}, {
                params: {
                    miniCashCabangId: this.selectedPengembalianItem.id,
                },
            }).then(() => {
                this.$bvModal.hide("modal-confirm-pengembalian-kas-kecil")
                this.$helper.toastSucc(this, "Pengembalian berhasil diproses.")
                this.selectedPengembalianItem = null
                this.getItems(this.currentPage)
            }).catch((error) => {
                this.$helper.parseError(this, error)
            }).finally(() => {
                this.isSubmittingPengembalian = false
            })
        },
        onClaimCash(item) {
            this.selectedClaimCashItem = item
            this.$bvModal.show("modal-confirm-claim-kas-kecil")
        },
        submitClaimCash() {
            if (!this.selectedClaimCashItem || !this.selectedClaimCashItem.id) return

            this.isSubmittingClaimCash = true
            this.$axios.post("mini-cash/claim", {}, {
                params: {
                    miniCashId: this.selectedClaimCashItem.id,
                },
            }).then(() => {
                this.$bvModal.hide("modal-confirm-claim-kas-kecil")
                this.$helper.toastSucc(this, "Kas berhasil diklaim.")
                this.selectedClaimCashItem = null
                this.getItems(this.currentPage)
            }).catch((error) => {
                this.$helper.parseError(this, error)
            }).finally(() => {
                this.isSubmittingClaimCash = false
            })
        },
        getCabangName(item) {
            if (item.cabang && item.cabang.name) return item.cabang.name
            if (item.cabangName) return item.cabangName
            if (item.namaCabang) return item.namaCabang
            return "-"
        },
        formatCurrency(value) {
            return `Rp. ${this.$helper.addThousandSeparator(Number(value || 0))}`
        },
        formatDateTime(value) {
            if (this.$helper.isNullOrEmpty(value)) return "-"
            if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return value
            return `${this.$helper.changeDateFormat(value)} ${this.$helper.changeDateFormatGetHour(value)}`
        },
        getApprovalCaAt(item) {
            return item.kcApprovedAt || item.openApprovedKcAt
        },
        getApprovalCaBy(item) {
            const approvedBy = item.kcApprovedBy || item.openApprovedKcBy
            const approvedAt = this.getApprovalCaAt(item)
            if (this.isEmptyHistoryValue(approvedBy) && !this.$helper.isNullOrEmpty(approvedAt)) return "Auto Approve"
            return this.formatHistoryBy(approvedBy)
        },
        hasApprovalCa(item) {
            return !this.$helper.isNullOrEmpty(this.getApprovalCaAt(item))
                || !this.isEmptyHistoryValue(item.kcApprovedBy)
                || !this.isEmptyHistoryValue(item.openApprovedKcBy)
        },
        hasHistoryInfo(name, date) {
            return !this.isEmptyHistoryValue(name) || !this.$helper.isNullOrEmpty(date)
        },
        isEmptyHistoryValue(value) {
            return this.$helper.isNullOrEmpty(value) || String(value).toLowerCase().includes("null")
        },
        formatHistoryBy(value) {
            if (this.isEmptyHistoryValue(value)) return "-"
            return value
        },
        getStatusLabel(status) {
            const normalizedStatus = this.normalizeStatus(status)
            if (normalizedStatus === STATUS_KAS_KECIL.PENDING) return "Menunggu Approval CA"
            if (normalizedStatus === STATUS_KAS_KECIL.BERJALAN) return "Berjalan"
            if (normalizedStatus === STATUS_KAS_KECIL.REQUEST_CLOSED) return "Menunggu Approval EC"
            if (normalizedStatus === STATUS_KAS_KECIL.APPROVED_KC) return "Disetujui KC"
            if (normalizedStatus === STATUS_KAS_KECIL.PENDING_PENGEMBALIAN) return "Menunggu Pengembalian"
            if (normalizedStatus === STATUS_KAS_KECIL.CLOSED) return "Ditutup"
            return "-"
        },
        getStatusClass(status) {
            const normalizedStatus = this.normalizeStatus(status)
            if (normalizedStatus === STATUS_KAS_KECIL.BERJALAN) return "status-box-md status-box-success"
            if (normalizedStatus === STATUS_KAS_KECIL.REQUEST_CLOSED) return "status-box-md status-box-warning"
            if (normalizedStatus === STATUS_KAS_KECIL.APPROVED_KC) return "status-box-md status-box-primary"
            if (normalizedStatus === STATUS_KAS_KECIL.PENDING_PENGEMBALIAN) return "status-box-md status-box-warning"
            if (normalizedStatus === STATUS_KAS_KECIL.CLOSED) return "status-box-md status-box-secondary"
            return "status-box-md status-box-warning"
        },
        normalizeStatus(status) {
            if (typeof status === "number") return status
            if (typeof status === "string") {
                if (status.includes("PENDING_PENGEMBALIAN")) return STATUS_KAS_KECIL.PENDING_PENGEMBALIAN
                if (status.includes("PENDING")) return STATUS_KAS_KECIL.PENDING
                if (status.includes("BERJALAN")) return STATUS_KAS_KECIL.BERJALAN
                if (status.includes("REQUEST_CLOSED")) return STATUS_KAS_KECIL.REQUEST_CLOSED
                if (status.includes("APPROVED_KC")) return STATUS_KAS_KECIL.APPROVED_KC
                if (status.includes("CLOSED")) return STATUS_KAS_KECIL.CLOSED
                return Number(status)
            }
            return status
        },
    },
    created() {
        this.init()
    },
    computed: {
        tabOptions() {
            return [
                { value: "list", label: "Daftar Kas Kecil" },
                { value: "void-pengembalian", label: "Void Pengembalian", count: this.voidPengembalianCount },
            ]
        },
        showCabangFilter() {
            return this.cabangOptions.length > 2
        },
        defaultCabangFilter() {
            return this.showCabangFilter ? this.cabangOptions[0] : this.cabangOptions[1] || null
        },
        hasMiniCashRequestPermission() {
            return this.$helper.hasPermissionMenuChildren("MRC")
        },
        hasMiniCashExportPermission() {
            return this.$helper.hasPermissionMenuChildren("MXL")
        },
        claimCashConfirmDesc() {
            const cabangName = this.selectedClaimCashItem ? this.getCabangName(this.selectedClaimCashItem) : "-"
            return `Apakah Anda yakin ingin klaim kas ${cabangName}?`
        },
        caConfirmDesc() {
            const cabangName = this.selectedCaItem ? this.getCabangName(this.selectedCaItem) : "cabang ini"
            return `Apakah Anda yakin ingin menyetujui CA ${cabangName}?`
        },
        pengembalianConfirmDesc() {
            const cabangName = this.selectedPengembalianItem ? this.getCabangName(this.selectedPengembalianItem) : "cabang ini"
            return `Apakah Anda yakin ingin memproses pengembalian ${cabangName}? Dana pengembalian akan otomatis dibuatkan kas masuk!`
        },
        historyTimeline() {
            const item = this.selectedHistoryItem
            if (!item) return []

            const entries = [
                { show: Boolean(item.createdBy || item.createdAt), icon: "fa-clipboard", color: "blue", title: "Pengajuan CA", by: item.createdBy || "-", at: this.formatDateTime(item.createdAt) },
                { show: this.hasApprovalCa(item), icon: "fa-check", color: "green", title: "Approval CA", by: this.getApprovalCaBy(item), at: this.formatDateTime(this.getApprovalCaAt(item)) },
                { show: this.hasHistoryInfo(item.claimedBy, item.claimedAt), icon: "fa-money", color: "orange", title: "Klaim Kas", by: this.formatHistoryBy(item.claimedBy), at: this.formatDateTime(item.claimedAt) },
                { show: this.hasHistoryInfo(item.requestClosedBy, item.requestClosedAt), icon: "fa-file-text-o", color: "yellow", title: "Pengajuan EC", by: this.formatHistoryBy(item.requestClosedBy), at: this.formatDateTime(item.requestClosedAt) },
                { show: this.hasHistoryInfo(item.approvalClosedKcUserBy, item.approvalClosedKcUserAt), icon: "fa-check-circle-o", color: "green", title: "Approval EC", by: this.formatHistoryBy(item.approvalClosedKcUserBy), at: this.formatDateTime(item.approvalClosedKcUserAt) },
                { show: this.hasHistoryInfo(item.refundedBy, item.refundedAt), icon: "fa-undo", color: "purple", title: "Proses Pengembalian", by: this.formatHistoryBy(item.refundedBy), at: this.formatDateTime(item.refundedAt) },
            ]
            return entries.filter((entry) => entry.show)
        },
    },
}
</script>

<style scoped>
.kas-kecil-nominal-row {
    align-items: center;
    display: flex;
    gap: 12px;
    justify-content: space-between;
}

.kas-kecil-history-line {
    align-items: baseline;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 3px;
}

.kas-kecil-history-line > span:first-child {
    color: #8a97a5;
}

.kas-kecil-history-line span {
    min-width: 58px;
}

.kas-kecil-history-line strong {
    font-weight: 500;
}

.kas-kecil-action-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

@media (max-width: 575.98px) {
    .kas-kecil-date-filter {
        flex-wrap: wrap;
    }
}

.btn-dokumen-ca {
    background-color: #00BFFF;
    border-color: #00BFFF;
    color: #ffffff;
}

.btn-dokumen-ca:hover,
.btn-dokumen-ca:active {
    background-color: #009ecc;
    border-color: #009ecc;
    color: #ffffff;
}

.btn-icon-spinner {
    height: 0.65rem;
    width: 0.65rem;
}
</style>
